import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, ArrowRight, Sparkles, Database, BrainCircuit, Cpu, Bot, Layers, GraduationCap } from "lucide-react";
import api from "@/lib/api";

const ICONS = { sparkles: Sparkles, database: Database, "brain-circuit": BrainCircuit, cpu: Cpu, bot: Bot };

export default function CertificationsHubPage() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/certifications").then((r) => setCerts(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="certifications-hub-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="certifications-hub-page">
      <div className="flex items-center gap-3 mb-2">
        <BadgeCheck className="w-6 h-6 text-indigo-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Certification Prep</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Get Certified</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl">
        Cheat sheets, flashcards, untimed practice tests, and a timed mock Exam Simulator for 5 real Salesforce certification tracks.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {certs.map((c, i) => (
          <motion.div
            key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}
            className="glass-panel rounded-2xl p-6 relative overflow-hidden flex flex-col"
            data-testid={`certification-card-${c.id}`}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${c.color}1a` }}>
              {(() => { const Icon = ICONS[c.icon] || Layers; return <Icon className="w-5 h-5" style={{ color: c.color }} />; })()}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: c.color }}>{c.level}</span>
            <h2 className="font-heading text-lg font-semibold text-white mt-1">{c.name}</h2>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{c.description}</p>

            <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
              <span>{c.flashcard_count} flashcards</span>
              <span>{c.question_count} questions</span>
            </div>
            {c.best_exam_score !== null && (
              <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400" data-testid={`certification-best-score-${c.id}`}>
                Best mock exam score: {c.best_exam_score}%
              </span>
            )}

            <Link
              to={`/certifications/${c.id}`} data-testid={`certification-enter-btn-${c.id}`}
              className="mt-5 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 px-4 py-2.5 rounded-lg transition-colors"
            >
              Start Prep <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        ))}
      </div>

      <Link to="/career" className="mt-10 flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300" data-testid="certifications-view-roadmap-link">
        <GraduationCap className="w-4 h-4" /> View your personalized AI Certification Roadmap in Career Center
      </Link>
    </div>
  );
}
